import { type ReactNode, createContext, useState } from 'react'

import {
  type SanityCombinedListingConfiguration,
  type SanityCombinedListingSettings,
} from '@data/sanity/queries/types/product'
import {
  type SanityCombinedListingProductFragment,
  type SanityCombinedListingProductVariantFragment,
} from '@data/sanity/queries/types/product'
import {
  useCombinedListing,
  useMemoryStateManager,
  useUrlStateManager,
} from './hooks'
import {
  type CombinedListingProductType,
  type CombinedListingStateManagerType,
} from './types'

interface ProductCombinedListingContextProps {
  combinedListingSettings?: SanityCombinedListingSettings
  combinedListingConfiguration?: SanityCombinedListingConfiguration
  combinedListingMainProductId?: number
  combinedListingProducts?: SanityCombinedListingProductFragment[]
  combinedListingVariants?: SanityCombinedListingProductVariantFragment[]
  combinedListingFrameSize?: string | null
  combinedListingArtworkSize?: string | null
  setCombinedListingStateManagerType: (
    stateManagerType: CombinedListingStateManagerType,
  ) => void
  setCombinedListingSettings: (
    combinedListingSettings?: SanityCombinedListingSettings,
  ) => void
  setCombinedListingConfiguration: (
    combinedListingConfiguration?: SanityCombinedListingConfiguration,
  ) => void
  setCombinedListingMainProductId: (productId: number) => void
  addCombinedListingProduct: (productId: number) => void
  setCombinedListingFrameSize: (newFrameSize: string | null) => void
  setCombinedListingArtworkSize: (newArtworkSize: string | null) => void
  clearCombinedListingProductType: (type: CombinedListingProductType) => void
  clearCombinedListing: () => void
}

interface StringsContextProviderProps {
  children: ReactNode
}

export const ProductCombinedListingContext =
  createContext<ProductCombinedListingContextProps>({
    setCombinedListingStateManagerType: () => null,
    setCombinedListingSettings: () => null,
    setCombinedListingConfiguration: () => null,
    setCombinedListingMainProductId: () => null,
    addCombinedListingProduct: () => null,
    setCombinedListingFrameSize: () => null,
    setCombinedListingArtworkSize: () => null,
    clearCombinedListingProductType: () => null,
    clearCombinedListing: () => null,
  })

export const ProductCombinedListingContextProvider = ({
  children,
}: StringsContextProviderProps) => {
  const [combinedListingStateManagerType, setCombinedListingStateManagerType] =
    useState<CombinedListingStateManagerType>('memory')
  const [combinedListingMainProductId, setCombinedListingMainProductId] =
    useState<number>()
  const [combinedListingConfiguration, setCombinedListingConfiguration] =
    useState<SanityCombinedListingConfiguration>()
  const [combinedListingSettings, setCombinedListingSettings] =
    useState<SanityCombinedListingSettings>()

  // Combined listing state manager that saves parameters
  const urlStateManager = useUrlStateManager()
  const memoryStateManager = useMemoryStateManager()
  const stateManager =
    combinedListingStateManagerType === 'url'
      ? urlStateManager
      : memoryStateManager

  const [
    combinedListingProducts,
    combinedListingVariants,
    combinedListingFrameSize,
    combinedListingArtworkSize,
    addCombinedListingProduct,
    setCombinedListingFrameSize,
    setCombinedListingArtworkSize,
    clearCombinedListingProductType,
    clearCombinedListing,
  ] = useCombinedListing(
    stateManager,
    combinedListingMainProductId,
    combinedListingConfiguration,
  )

  return (
    <ProductCombinedListingContext.Provider
      value={{
        combinedListingSettings,
        combinedListingConfiguration,
        combinedListingMainProductId,
        combinedListingProducts,
        combinedListingVariants,
        combinedListingFrameSize,
        combinedListingArtworkSize,
        setCombinedListingStateManagerType,
        setCombinedListingSettings,
        setCombinedListingConfiguration,
        setCombinedListingMainProductId,
        addCombinedListingProduct,
        setCombinedListingFrameSize,
        setCombinedListingArtworkSize,
        clearCombinedListingProductType,
        clearCombinedListing,
      }}
    >
      {children}
    </ProductCombinedListingContext.Provider>
  )
}
